import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { MenuProvider } from '#pplx/frontend/aether/components/internal/menu/MenuContext.js';
import { useMenuFooterRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuFooterRegistry.js';
import { useMenuHeaderRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuHeaderRegistry.js';
import { useMenuItemRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuItemRegistry.js';
import { useMenuSubmenuRegistry } from '#pplx/frontend/aether/components/internal/menu/useMenuSubmenuRegistry.js';
import { ScrollArea } from '#pplx/frontend/aether/internal/scroll-area/ScrollArea.js';
import { useThrottle } from '#pplx/frontend/common/hooks/useThrottle.js';
import { assertUnreachableValue } from '#pplx/frontend/common/type_utils/unreachableValue.js';
// eslint-disable-next-line pplx/no-internal-imports -- Generated CSS contract for this same internal package.
import { styles } from '#pplx_generated/pplx/frontend/aether/components/internal/menu/MenuOverlay.module.css.js';
import classNames from 'classnames';
import { useCallback } from 'react';
function getMenuOverlayClassNameByVariant(variant) {
    switch (variant) {
        case 'default':
            return 'bg-raised flex flex-col shadow-md border-transparent p-1 rounded-xl';
        case 'attached':
            return 'bg-raised flex flex-col border-t border-t-soft rounded-b-2xl rounded-t-none p-0 shadow-md [clip-path:inset(0_-64px_-64px)] forced-colors:border forced-colors:border-[CanvasText]';
        case 'embedded':
            return 'flex flex-col min-h-0 overflow-hidden bg-transparent';
        default:
            return assertUnreachableValue(variant);
    }
}
/**
 * Aether-internal: this is meant to be used by Menu and MenuSubmenu components only.
 *
 * MenuOverlay provides a hierarchical context for submenu management.
 * Each MenuOverlay maintains its own registry of direct child submenus.
 * When scrolling occurs, only the direct children are closed, not the menu itself.
 *
 * Header and footer compound components hoist their content outside the scrollable region.
 */
// TODO: Promote as a general purpose overlay container component that includes
// the visual styles we use for any overlays across the app.
export function MenuOverlay({ children, maxHeightPx = 300, minWidthPx, maxWidthPx, variant = 'default', }) {
    const submenus = useMenuSubmenuRegistry();
    const menuItems = useMenuItemRegistry();
    const { footer, registry: footerRegistry } = useMenuFooterRegistry();
    const { header, registry: headerRegistry } = useMenuHeaderRegistry();
    const handleScroll = useCallback(() => {
        submenus.closeAll();
    }, [submenus]);
    // Throttle scroll events to 100ms for performance
    const throttledHandleScroll = useThrottle(handleScroll, 100);
    const overlayStyle = {
        maxWidth: maxWidthPx,
        minWidth: minWidthPx,
    };
    return (_jsx(MenuProvider, { submenus: submenus, menuItems: menuItems, footerRegistry: footerRegistry, headerRegistry: headerRegistry, hasWidthConstraint: maxWidthPx != null, children: _jsxs("div", { className: classNames(styles.overlay, getMenuOverlayClassNameByVariant(variant)), style: overlayStyle, children: [header != null && _jsx("div", { className: "shrink-0", children: header }), _jsx(ScrollArea, { maxHeight: `${maxHeightPx}px`, onScroll: throttledHandleScroll, fadeEdges: variant === 'embedded', children: children }), footer != null && _jsx("div", { className: "shrink-0", children: footer })] }) }));
}
//# sourceMappingURL=MenuOverlay.js.map